import { Injectable } from '@angular/core';
import { ApiService } from '@services';
import { map, Observable } from 'rxjs';
import {
  OrderFacets,
  OrderFilters,
  OrderSuggestion,
  OrdersExportCount,
  OrdersExportParams,
  OrdersPaginated,
  PaginationPayload,
} from '@interfaces';
import {
  DeliveredOrder,
  DriverOrder,
  OrderById,
  PatchShippingDetail,
} from '@models';
import { APIRoutes } from '@constants';
import { buildHttpParams, mergeClientIds } from '@utils';
import { HttpParams, HttpResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class SaleOrdersService {
  private url = APIRoutes.SALE_ORDERS;

  constructor(private apiService: ApiService) {}

  public getUndeliveredOrders(
    params: PaginationPayload & Partial<OrderFilters>,
  ): Observable<OrdersPaginated> {
    return this.apiService.get(`v2/${this.url}/sellers/pending-approval`, {
      params: buildHttpParams({
        _end: params.page * params.perPage,
        _order: 'DESC',
        _sort: 'id',
        _start: params.page * params.perPage - params.perPage,
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
        clientIds: mergeClientIds(params.ferreteriaIds, params.aliasIds),
        orderNumber: params.orderNumber,
        urgency: params.urgency,
      }),
    });
  }

  public getFacets(
    dateFrom?: string,
    dateTo?: string,
  ): Observable<OrderFacets> {
    return this.apiService.get(`v2/${this.url}/sellers/facets`, {
      params: buildHttpParams({ dateFrom, dateTo }),
    });
  }

  // Shared by getOrdersExportFile and getOrdersExportCount — one
  // serialization path makes the two calls byte-identical by construction
  // (B-27), rather than relying on two independent buildHttpParams calls
  // staying in sync.
  private buildExportHttpParams(params: OrdersExportParams): HttpParams {
    return buildHttpParams({
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      clientIds: mergeClientIds(params.ferreteriaIds, params.aliasIds),
      urgency: params.urgency,
      orderNumber: params.orderNumber,
    });
  }

  // Shared by getOrdersExportFile and getShippingGuideFile — both blob
  // downloads extract { blob, contentType } from the raw HttpResponse the
  // same way; a single mapper keeps them from drifting apart.
  private toBlobResponse() {
    return map((res: HttpResponse<Blob>) => ({
      blob: res.body ?? new Blob(),
      contentType:
        res.headers.get('Content-Type') || 'application/octet-stream',
    }));
  }

  public getOrdersExportCount(
    params: OrdersExportParams,
  ): Observable<OrdersExportCount> {
    return this.apiService.get(`v2/${this.url}/sellers/export-excel/count`, {
      params: this.buildExportHttpParams(params),
    });
  }

  public getOrdersExportFile(
    params: OrdersExportParams,
  ): Observable<{ blob: Blob; contentType: string }> {
    return this.apiService
      .get(`v2/${this.url}/sellers/export-excel`, {
        params: this.buildExportHttpParams(params),
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(this.toBlobResponse());
  }

  public getOrderNumberSuggestions(
    orderNumber: string,
    filters?: Pick<
      OrderFilters,
      'dateFrom' | 'dateTo' | 'ferreteriaIds' | 'aliasIds'
    >,
  ): Observable<OrderSuggestion[]> {
    return this.apiService.get(`v2/${this.url}/sellers/autocomplete`, {
      params: buildHttpParams({
        orderNumber,
        dateFrom: filters?.dateFrom,
        dateTo: filters?.dateTo,
        clientIds: mergeClientIds(filters?.ferreteriaIds, filters?.aliasIds),
      }),
    });
  }

  public getOrderById(id: string): Observable<OrderById> {
    return this.apiService.get(
      `${this.url}/undelivered/search/sale-order-id?id=${id}`,
    );
  }

  public getDeliveredOrders(
    year: number,
  ): Observable<Record<string, DeliveredOrder[]>> {
    return this.apiService.get(`v2/${this.url}/sellers/history?year=${year}`);
  }

  public rejectOrder(saleOrderId: string): Observable<void> {
    return this.apiService.patch(
      `${this.url}/sellers/${saleOrderId}/cancelled`,
      {},
    );
  }

  public patchShippingDetails(
    shippingDetail: PatchShippingDetail,
  ): Observable<void> {
    const formData = new FormData();
    if (shippingDetail.file) {
      formData.append('file', shippingDetail.file);
    }
    formData.append('observation', shippingDetail.observation);
    return this.apiService.patch(
      `v2/${this.url}/${shippingDetail.saleOrderId}/shipping-details`,
      formData,
    );
  }

  public patchDeliveredOrder(
    saleOrderId: string,
    receipt?: File,
  ): Observable<void> {
    const formData = new FormData();
    formData.append('saleOrderId', saleOrderId);
    if (receipt) {
      formData.append('Receipt', receipt);
    }
    return this.apiService.patch(`v2/${this.url}/sellers/approved`, formData);
  }

  public getUndeliverdOrdersByDate(date: string): Observable<DriverOrder[]> {
    return this.apiService.get(
      `v2/${this.url}/sellers/non-canceled/by-date?shippingDate=${date}`,
    );
  }

  public patchDeliveryCode(
    saleOrderId: string,
    code: string,
  ): Observable<void> {
    return this.apiService.patch(
      `v2/${this.url}/${saleOrderId}/delivered/delivery-code/${code}`,
      {},
    );
  }

  public patchDeliveryPhoto(saleOrderId: string, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('deliveryPhoto', file);
    return this.apiService.patch(
      `v2/${this.url}/${saleOrderId}/delivered/delivery-photo`,
      formData,
    );
  }

  public getShippingGuideFile(
    orderNumber: string,
  ): Observable<{ blob: Blob; contentType: string }> {
    return this.apiService
      .get(
        `v2/${this.url}/seller/shipping-guide-file?orderNumber=${encodeURIComponent(orderNumber)}`,
        {
          headers: {
            Accept: 'application/pdf,image/*,application/zip',
          },
          responseType: 'blob',
          observe: 'response',
        },
      )
      .pipe(this.toBlobResponse());
  }
}
